import { useCallback } from 'react';
import { Queue } from '@shared/util/hooks/useQueue';
import { uuid } from '@shared/util/uuid';
import { AlertProps } from './ModalAlert';
import { ConfirmProps } from './ModalConfirm';

// delay before removing item from queue and showing the next one
const QUEUE_DELAY = 300;

export type AlertCreator = (props: AlertProps) => Promise<void>;
export type ConfirmCreator = (props: ConfirmProps) => Promise<boolean>;

export interface QueuedAlert extends AlertProps {
  id: string;
  type: 'alert';
}

export interface QueuedConfirm extends ConfirmProps {
  id: string;
  type: 'confirm';
}

export type ModalQueueItem = QueuedAlert | QueuedConfirm;

export function useAlertCreator({ append, remove }: Queue<ModalQueueItem>): AlertCreator {
  return useCallback(
    async (alertProps: AlertProps) => {
      return new Promise(resolve => {
        const alertId = uuid();
        const alert: QueuedAlert = {
          ...alertProps,
          id: alertId,
          type: 'alert',
          onClose: async () => {
            await alertProps.onClose?.();
            resolve();
            setTimeout(() => remove(alert), QUEUE_DELAY);
          },
        };
        append(alert);
      });
    },
    [append, remove],
  );
}

export function useConfirmCreator({ append, remove }: Queue<ModalQueueItem>): ConfirmCreator {
  return useCallback(
    async (confirmProps: ConfirmProps) => {
      return new Promise(resolve => {
        const confirmId = uuid();
        const confirm: QueuedConfirm = {
          ...confirmProps,
          id: confirmId,
          type: 'confirm',
          onConfirm: async () => {
            await confirmProps.onConfirm?.();
            resolve(true);
          },
          onClose: async () => {
            await confirmProps.onClose?.();
            resolve(false);
            setTimeout(() => remove(confirm), QUEUE_DELAY);
          },
        };
        append(confirm);
      });
    },
    [append, remove],
  );
}
